import { StandardProps } from "@/types/types";
import { FoundItLink } from "@/types/foundIt";
import { useRouter } from "next/router";
import { SecondaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { useMemo, useRef, useEffect, useState } from "react";
import { useRouteParams } from "@/hooks/useRouteParams";
import styles from "./FoundItLinks.module.scss";
import { useFeatureFlags } from "@/flags/flags";

type FoundItLinksComponentProps = Pick<
  StandardProps,
  "configProps" | "foundItData"
>;

const MIN_LINKS_TO_SHOW = 2;
const MAX_LINKS_TO_SHOW = 12;

export function FoundItLinks({
  foundItData,
  configProps,
}: FoundItLinksComponentProps) {
  const linksFoundItData = foundItData.links;
  const router = useRouter();
  const { isFoundItFlagOn } = useFeatureFlags();

  const { queryWithoutPageOrChunk } = useRouteParams();

  const maxItems = (configProps?.maxItems as number) || MAX_LINKS_TO_SHOW;

  const links: FoundItLink[] = useMemo(() => {
    return linksFoundItData?.slice(0, maxItems) || [];
  }, [linksFoundItData, maxItems]);

  // --- Overflow detection logic ---
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const checkOverflow = () => {
      setIsOverflowing(wrapper.scrollWidth > wrapper.clientWidth + 1); // +1 for rounding
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [links.length]);

  if (
    !isFoundItFlagOn.value ||
    !linksFoundItData ||
    linksFoundItData.length < MIN_LINKS_TO_SHOW
  ) {
    return null;
  }

  return (
    <div
      data-component="FoundItLinks"
      className={styles.foundItLinks}
      role="region"
      aria-labelledby="foundit-links-title"
    >
      <h2 id="foundit-links-title" className={styles.title}>
        Discover more
      </h2>
      <div className={styles.linksContainer}>
        <div
          className={
            isOverflowing
              ? `${styles.linksWrapper} ${styles.overflowing}`
              : styles.linksWrapper
          }
          ref={wrapperRef}
        >
          {links.map((link, i) => {
            const { AnchorText, DestinationUrl } = link;
            return (
              <SecondaryButton
                key={i}
                className={styles.linkButton}
                size="small"
                tag="a"
                type="button"
                href={DestinationUrl}
              >
                {AnchorText}
              </SecondaryButton>
            );
          })}
        </div>
      </div>
    </div>
  );
}
